import {
  CreateFreedomToSpeakUpConcernPayload,
  freedomToSpeakUpApiHandler,
  FreedomToSpeakUpConcernDetail,
  FreedomToSpeakUpConcernsListData,
  FreedomToSpeakUpResponsesData,
  mapFreedomToSpeakUpConcernsList,
} from '@api/freedomToSpeakUpApiHandler';
import { ApiResponse } from '@interfaces/index';
import { FreedomToSpeakUpConcernListItem } from '@shared/types/freedomToSpeakUpConcern';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const FREEDOM_TO_SPEAK_UP_QUERY_KEYS = {
  formOptions: ['freedomToSpeakUp', 'formOptions'] as const,
  myConcerns: ['freedomToSpeakUp', 'myConcerns'] as const,
  concern: (concernId: number) => ['freedomToSpeakUp', 'concern', concernId] as const,
  responses: (concernId: number) => ['freedomToSpeakUp', 'responses', concernId] as const,
};

export const useFreedomToSpeakUpFormOptions = (enabled = true) =>
  useQuery({
    queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.formOptions,
    queryFn: async () => {
      const response = await freedomToSpeakUpApiHandler.getFormOptions();

      if (!response.success) {
        throw new Error(response.message || 'Failed to load concern form options');
      }

      return response;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useMyFreedomToSpeakUpConcerns = (enabled = true) =>
  useQuery<FreedomToSpeakUpConcernListItem[]>({
    queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.myConcerns,
    queryFn: async () => {
      const response: ApiResponse<FreedomToSpeakUpConcernsListData> =
        await freedomToSpeakUpApiHandler.getMyConcerns();

      return response.success ? mapFreedomToSpeakUpConcernsList(response.responseObject) : [];
    },
    enabled,
  });

export const useFreedomToSpeakUpConcern = (concernId: number, enabled: boolean) =>
  useQuery<ApiResponse<FreedomToSpeakUpConcernDetail>>({
    queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.concern(concernId),
    queryFn: () => freedomToSpeakUpApiHandler.getConcernById(concernId),
    enabled: enabled && concernId > 0,
  });

export const useFreedomToSpeakUpResponses = (concernId: number, enabled: boolean) =>
  useQuery<ApiResponse<FreedomToSpeakUpResponsesData>>({
    queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.responses(concernId),
    queryFn: () => freedomToSpeakUpApiHandler.getResponses(concernId),
    enabled: enabled && concernId > 0,
  });

export const useMarkFreedomToSpeakUpResponsesSeen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ concernId }: { concernId: number }) =>
      freedomToSpeakUpApiHandler.markResponsesSeen(concernId, { updateAs: 'user' }),
    onSuccess: (_data, { concernId }) => {
      queryClient.invalidateQueries({ queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.myConcerns });
      queryClient.invalidateQueries({
        queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.responses(concernId),
      });
    },
  });
};

export const useCreateFreedomToSpeakUpResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ concernId, message }: { concernId: number; message: string }) =>
      freedomToSpeakUpApiHandler.createResponse(concernId, message),
    onSuccess: (_data, { concernId }) => {
      queryClient.invalidateQueries({
        queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.responses(concernId),
      });
      queryClient.invalidateQueries({ queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.myConcerns });
      queryClient.invalidateQueries({
        queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.concern(concernId),
      });
    },
  });
};

export const useCreateFreedomToSpeakUpConcern = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateFreedomToSpeakUpConcernPayload) =>
      freedomToSpeakUpApiHandler.createConcern(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FREEDOM_TO_SPEAK_UP_QUERY_KEYS.myConcerns });
    },
  });
};
